/**
 * Everything above the grid: search, filters, columns, saved views, export.
 *
 * The organising idea is that a filter is a **chip you can read**. `risk_score:gte:15` is
 * how it travels; "Risk score is at least 15" is what it says on screen, and clicking the
 * chip reopens the editor with those values in it. A filter you cannot read is a filter
 * people stop trusting and work around.
 *
 * Menus close on Escape and on a click outside, and each one is a `<details>`-free popover
 * with real focus management, because this row is used constantly by people who know exactly
 * what they want and do not want to reach for the mouse twice.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ColumnInfo } from '@/api/types'
import { Icon } from '@/components/Icon'
import type { Filter, SavedView } from '@/features/registers/useGridView'

/** Operators the API accepts per column kind — mirrors `FILTERABLE_KINDS` on the server. */
const OPERATORS: Record<string, string[]> = {
  text: ['contains', 'eq', 'ne', 'empty', 'not_empty'],
  longtext: ['contains', 'eq', 'ne', 'empty', 'not_empty'],
  integer: ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'empty', 'not_empty'],
  number: ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'empty', 'not_empty'],
  percent: ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'empty', 'not_empty'],
  money: ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'empty', 'not_empty'],
  date: ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'empty', 'not_empty'],
  datetime: ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'empty', 'not_empty'],
  boolean: ['eq'],
  select: ['eq', 'ne', 'empty', 'not_empty'],
  reference: ['eq', 'ne', 'empty', 'not_empty'],
}

/** Operators that take no value, so the editor hides its input. */
const VALUELESS = new Set(['empty', 'not_empty'])

function filterable(column: ColumnInfo): boolean {
  return column.kind in OPERATORS
}

export function GridToolbar({
  columns,
  filters,
  hidden,
  views,
  activeView,
  search,
  matched,
  exportHref,
  onSearch,
  onAddFilter,
  onRemoveFilter,
  onClearFilters,
  onToggleColumn,
  onShowAllColumns,
  onSaveView,
  onApplyView,
  onDeleteView,
}: {
  columns: ColumnInfo[]
  filters: Filter[]
  hidden: string[]
  views: SavedView[]
  activeView: string | null
  search: string
  matched: number | null
  exportHref: string
  onSearch: (value: string) => void
  onAddFilter: (filter: Filter) => void
  onRemoveFilter: (index: number) => void
  onClearFilters: () => void
  onToggleColumn: (name: string) => void
  onShowAllColumns: () => void
  onSaveView: (name: string) => void
  onApplyView: (view: SavedView) => void
  onDeleteView: (id: string) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<'filter' | 'columns' | 'views' | null>(null)
  const [editing, setEditing] = useState<{ index: number; filter: Filter } | null>(null)
  const root = useRef<HTMLDivElement>(null)

  // One handler for both ways out of a menu. Without the outside-click a popover stays open
  // behind whatever the person clicked next, which reads as the screen being stuck.
  useEffect(() => {
    if (open === null && editing === null) return
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') {
          setOpen(null)
          setEditing(null)
        }
        return
      }
      if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(null)
        setEditing(null)
      }
    }
    document.addEventListener('keydown', close)
    document.addEventListener('mousedown', close)
    return () => {
      document.removeEventListener('keydown', close)
      document.removeEventListener('mousedown', close)
    }
  }, [open, editing])

  const filterableColumns = columns.filter(filterable)
  const labelOf = (name: string) =>
    columns.find((column) => column.name === name)?.label ?? name

  return (
    <div className="toolbar" ref={root} data-testid="grid-toolbar">
      <div className="toolbar__row">
        <label className="toolbar__search">
          <Icon name="search" size={15} className="toolbar__searchIcon" />
          <input
            type="search"
            className="toolbar__input"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={t('grid.searchPlaceholder')}
            aria-label={t('grid.searchPlaceholder')}
            data-testid="grid-search"
          />
        </label>

        <div className="toolbar__actions">
          <ToolbarButton
            icon="filter"
            label={t('grid.filter')}
            count={filters.length}
            active={open === 'filter'}
            onClick={() => setOpen(open === 'filter' ? null : 'filter')}
            testid="grid-filter-open"
          />
          <ToolbarButton
            icon="registers"
            label={t('grid.columns')}
            count={hidden.length}
            active={open === 'columns'}
            onClick={() => setOpen(open === 'columns' ? null : 'columns')}
            testid="grid-columns-open"
          />
          <ToolbarButton
            icon="pin"
            label={t('grid.views')}
            count={views.length}
            active={open === 'views'}
            onClick={() => setOpen(open === 'views' ? null : 'views')}
            testid="grid-views-open"
          />
          {/* A real link, not a fetch. The browser's own download handles the streaming
              response, the filename and the progress, and none of that is worth
              reimplementing badly in JavaScript. */}
          <a className="toolbar__button" href={exportHref} data-testid="grid-export">
            <Icon name="export" size={15} />
            <span>{t('grid.export')}</span>
          </a>
        </div>
      </div>

      {/* The chips. Readable, individually removable, and clickable to re-edit. */}
      {filters.length > 0 ? (
        <div className="chips" data-testid="grid-chips">
          {filters.map((filter, index) => (
            <button
              key={`${filter.column}-${filter.operator}-${index}`}
              type="button"
              className="chipFilter"
              onClick={() => setEditing({ index, filter })}
              data-testid="grid-chip"
            >
              <span className="chipFilter__text">
                {t(`grid.op.${filter.operator}`, {
                  column: labelOf(filter.column),
                  value: filter.value,
                })}
              </span>
              <span
                className="chipFilter__x"
                role="button"
                tabIndex={0}
                aria-label={t('grid.removeFilter')}
                onClick={(event) => {
                  event.stopPropagation()
                  onRemoveFilter(index)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    event.stopPropagation()
                    onRemoveFilter(index)
                  }
                }}
                data-testid="grid-chip-remove"
              >
                <Icon name="close" size={12} strokeWidth={2.4} />
              </span>
            </button>
          ))}
          <button
            type="button"
            className="chips__clear"
            onClick={onClearFilters}
            data-testid="grid-chips-clear"
          >
            {t('grid.clearAll')}
          </button>
          {matched !== null ? (
            <span className="chips__count numeric" data-testid="grid-matched">
              {t('grid.matched', { count: matched })}
            </span>
          ) : null}
        </div>
      ) : null}

      {open === 'filter' ? (
        <FilterEditor
          columns={filterableColumns}
          onCancel={() => setOpen(null)}
          onApply={(filter) => {
            onAddFilter(filter)
            setOpen(null)
          }}
        />
      ) : null}

      {editing ? (
        <FilterEditor
          columns={filterableColumns}
          initial={editing.filter}
          onCancel={() => setEditing(null)}
          onApply={(filter) => {
            onRemoveFilter(editing.index)
            onAddFilter(filter)
            setEditing(null)
          }}
        />
      ) : null}

      {open === 'columns' ? (
        <div className="popover" role="dialog" data-testid="grid-columns">
          <p className="popover__title">{t('grid.columnsTitle')}</p>
          <div className="popover__list" data-scroll="1">
            {columns.map((column) => (
              <label key={column.name} className="popover__check">
                <input
                  type="checkbox"
                  checked={!hidden.includes(column.name)}
                  onChange={() => onToggleColumn(column.name)}
                  data-testid={`grid-column-${column.name}`}
                />
                <span>{column.label}</span>
              </label>
            ))}
          </div>
          <div className="popover__foot">
            <button
              type="button"
              className="button button--quiet button--small"
              onClick={onShowAllColumns}
              data-testid="grid-columns-all"
            >
              {t('grid.showAll')}
            </button>
          </div>
        </div>
      ) : null}

      {open === 'views' ? (
        <ViewsMenu
          views={views}
          activeView={activeView}
          onApply={(view) => {
            onApplyView(view)
            setOpen(null)
          }}
          onSave={(name) => {
            onSaveView(name)
            setOpen(null)
          }}
          onDelete={onDeleteView}
        />
      ) : null}
    </div>
  )
}

function ToolbarButton({
  icon,
  label,
  count,
  active,
  onClick,
  testid,
}: {
  icon: 'filter' | 'registers' | 'pin'
  label: string
  count: number
  active: boolean
  onClick: () => void
  testid: string
}) {
  return (
    <button
      type="button"
      className="toolbar__button"
      aria-expanded={active}
      data-active={active || undefined}
      onClick={onClick}
      data-testid={testid}
    >
      <Icon name={icon} size={15} />
      <span>{label}</span>
      {count > 0 ? <span className="toolbar__badge numeric">{count}</span> : null}
    </button>
  )
}

function FilterEditor({
  columns,
  initial,
  onApply,
  onCancel,
}: {
  columns: ColumnInfo[]
  initial?: Filter
  onApply: (filter: Filter) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const first = useRef<HTMLSelectElement>(null)
  const [column, setColumn] = useState(initial?.column ?? columns[0]?.name ?? '')
  const [operator, setOperator] = useState(initial?.operator ?? '')
  const [value, setValue] = useState(initial?.value ?? '')

  const chosen = columns.find((entry) => entry.name === column)
  // Memoised because the effect below depends on it. A fresh `[]` on every render would
  // re-run that effect on every render, and it calls `setOperator` — which renders again.
  const available = useMemo(() => (chosen ? (OPERATORS[chosen.kind] ?? []) : []), [chosen])

  // Keep the operator legal when the column changes. Carrying `gte` from a score onto a
  // text column would send a filter the API refuses, and the person would see an error for
  // something they never chose.
  useEffect(() => {
    if (!available.includes(operator)) setOperator(available[0] ?? '')
  }, [available, operator])

  useEffect(() => {
    first.current?.focus()
  }, [])

  const needsValue = !VALUELESS.has(operator)
  const incomplete = !column || !operator || (needsValue && value.trim() === '')

  return (
    <div className="popover popover--wide" role="dialog" data-testid="grid-filter-editor">
      <p className="popover__title">{initial ? t('grid.editFilter') : t('grid.addFilter')}</p>

      <div className="popover__grid">
        <label className="field">
          <span className="field__label">{t('grid.column')}</span>
          <select
            ref={first}
            className="field__input"
            value={column}
            onChange={(event) => setColumn(event.target.value)}
            data-testid="filter-column"
          >
            {columns.map((entry) => (
              <option key={entry.name} value={entry.name}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">{t('grid.condition')}</span>
          <select
            className="field__input"
            value={operator}
            onChange={(event) => setOperator(event.target.value)}
            data-testid="filter-operator"
          >
            {available.map((name) => (
              <option key={name} value={name}>
                {t(`grid.opName.${name}`)}
              </option>
            ))}
          </select>
        </label>

        {needsValue ? (
          <label className="field">
            <span className="field__label">{t('grid.value')}</span>
            <input
              className="field__input"
              type={chosen?.kind === 'date' ? 'date' : 'text'}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !incomplete) {
                  onApply({ column, operator, value: value.trim() })
                }
              }}
              data-testid="filter-value"
            />
          </label>
        ) : null}
      </div>

      <div className="popover__foot">
        <button type="button" className="button button--quiet button--small" onClick={onCancel}>
          {t('edit.cancel')}
        </button>
        <button
          type="button"
          className="button button--primary button--small"
          disabled={incomplete}
          onClick={() => onApply({ column, operator, value: needsValue ? value.trim() : '' })}
          data-testid="filter-apply"
        >
          {t('grid.apply')}
        </button>
      </div>
    </div>
  )
}

function ViewsMenu({
  views,
  activeView,
  onApply,
  onSave,
  onDelete,
}: {
  views: SavedView[]
  activeView: string | null
  onApply: (view: SavedView) => void
  onSave: (name: string) => void
  onDelete: (id: string) => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')

  return (
    <div className="popover" role="dialog" data-testid="grid-views">
      <p className="popover__title">{t('grid.viewsTitle')}</p>

      {views.length === 0 ? (
        <p className="popover__empty">{t('grid.noViews')}</p>
      ) : (
        <div className="popover__list" data-scroll="1">
          {views.map((view) => (
            <div key={view.id} className="popover__row" data-testid="grid-view">
              <button
                type="button"
                className="popover__rowMain"
                data-active={view.id === activeView || undefined}
                onClick={() => onApply(view)}
                data-testid="grid-view-apply"
              >
                <Icon name="pin" size={13} />
                <span>{view.name}</span>
                <span className="popover__rowMeta numeric">
                  {t('grid.viewMeta', { count: view.filters.length })}
                </span>
              </button>
              <button
                type="button"
                className="popover__rowX"
                aria-label={t('grid.deleteView')}
                onClick={() => onDelete(view.id)}
                data-testid="grid-view-delete"
              >
                <Icon name="close" size={12} strokeWidth={2.4} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="popover__foot popover__foot--split">
        <input
          className="field__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && name.trim()) {
              onSave(name)
              setName('')
            }
          }}
          placeholder={t('grid.nameThisView')}
          aria-label={t('grid.nameThisView')}
          data-testid="grid-view-name"
        />
        <button
          type="button"
          className="button button--primary button--small"
          disabled={!name.trim()}
          onClick={() => {
            onSave(name)
            setName('')
          }}
          data-testid="grid-view-save"
        >
          {t('grid.saveView')}
        </button>
      </div>
    </div>
  )
}
